import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Loader2, UserCog, Trash2, ScrollText, Users2, Search, Mail, Phone,
} from "lucide-react";

const EMPTY = {
  nombre: "", cedula: "", carnet_colegio: "", especialidad: "",
  telefono: "", email: "", es_notario: false, codigo_notario: "", activo: true,
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

export default function AbogadosTab() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=new, obj=edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () =>
    api.get("/abogados")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Error cargando abogados"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setDialogOpen(true); };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/abogados/${editing.id}`, form);
        toast.success("Abogado actualizado");
      } else {
        await api.post("/abogados", form);
        toast.success("Abogado agregado");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/abogados/${deleteTarget.id}`);
      toast.success("Abogado eliminado");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const filtered = items.filter((a) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return [a.nombre, a.especialidad, a.email, a.cedula, a.carnet_colegio]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div className="space-y-5" data-testid="abogados-tab">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
          <Input
            data-testid="abogados-search"
            placeholder="Buscar por nombre, cédula, especialidad..."
            value={query} onChange={(e) => setQuery(e.target.value)}
            className={`${inputCls} pl-10`}
          />
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            data-testid="abogados-add-button"
            className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Agregar abogado
          </Button>
        )}
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#6B5D57]">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Users2 className="h-8 w-8 text-[#C19B6C] mx-auto" strokeWidth={1.4} />
            <p className="mt-3 text-sm text-[#6B5D57]">
              {items.length === 0 ? "Aún no hay abogados asociados." : "Sin resultados."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                <TableHead className="text-[#3d2a2a] font-semibold">Abogado</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Especialidad</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Contacto</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
                {canEdit && <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`abogado-row-${a.id}`} className="border-b border-[#E5DFD3] hover:bg-[#FBF9F4]">
                  <TableCell>
                    <div className="font-medium text-[#1A0810]">{a.nombre}</div>
                    <div className="text-xs text-[#6B5D57] mt-0.5">
                      {a.cedula && <>Céd. {a.cedula}</>}
                      {a.carnet_colegio && <span> · Carnet {a.carnet_colegio}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#1A0810]">{a.especialidad || "—"}</div>
                    {a.es_notario && (
                      <Badge variant="outline" className="mt-1 border-[#C19B6C]/60 text-[#7A1E2E] bg-[#F5EEE0]">
                        <ScrollText className="h-3 w-3 mr-1" strokeWidth={1.8} />
                        Notario {a.codigo_notario && `· ${a.codigo_notario}`}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs text-[#3d2a2a] space-y-0.5">
                      {a.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-[#6B5D57]" strokeWidth={1.8} /> {a.email}</div>}
                      {a.telefono && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-[#6B5D57]" strokeWidth={1.8} /> {a.telefono}</div>}
                      {!a.email && !a.telefono && <span className="text-[#6B5D57]">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {a.activo ? (
                      <Badge className="bg-[#EEF6EE] text-[#1F5B2E] hover:bg-[#EEF6EE] border border-[#1F5B2E]/20">Activo</Badge>
                    ) : (
                      <Badge className="bg-[#F5EEE0] text-[#6B5D57] hover:bg-[#F5EEE0] border border-[#E5DFD3]">Inactivo</Badge>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`abogado-edit-${a.id}`} className="h-8 w-8 hover:bg-[#F5EEE0]">
                          <UserCog className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(a)} data-testid={`abogado-delete-${a.id}`} className="h-8 w-8 hover:bg-[#FBEDEF]">
                          <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#FBF9F4] border-[#E5DFD3]" data-testid="abogado-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">
              {editing ? "Editar abogado" : "Agregar abogado asociado"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="md:col-span-2">
              <Label className={labelCls}>Nombre completo *</Label>
              <Input data-testid="abogado-form-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={`${inputCls} mt-2`} placeholder="Lic. Carlos Vega Rojas" />
            </div>
            <div>
              <Label className={labelCls}>Cédula</Label>
              <Input data-testid="abogado-form-cedula" value={form.cedula} onChange={(e) => setForm({ ...form, cedula: e.target.value })} className={`${inputCls} mt-2`} placeholder="1-1234-5678" />
            </div>
            <div>
              <Label className={labelCls}>Carnet del Colegio</Label>
              <Input data-testid="abogado-form-carnet" value={form.carnet_colegio} onChange={(e) => setForm({ ...form, carnet_colegio: e.target.value })} className={`${inputCls} mt-2`} placeholder="12345" />
            </div>
            <div>
              <Label className={labelCls}>Especialidad</Label>
              <Input data-testid="abogado-form-especialidad" value={form.especialidad} onChange={(e) => setForm({ ...form, especialidad: e.target.value })} className={`${inputCls} mt-2`} placeholder="Derecho Penal" />
            </div>
            <div>
              <Label className={labelCls}>Teléfono</Label>
              <Input data-testid="abogado-form-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={`${inputCls} mt-2`} placeholder="+506 8000-0000" />
            </div>
            <div className="md:col-span-2">
              <Label className={labelCls}>Correo electrónico</Label>
              <Input data-testid="abogado-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-2`} placeholder="cvega@bufete.cr" />
            </div>

            <div className="md:col-span-2 rounded-lg bg-white border border-[#E5DFD3] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-[#1A0810]">Es notario público</div>
                  <div className="text-xs text-[#6B5D57] mt-0.5">Activar si el abogado ejerce como notario.</div>
                </div>
                <Switch
                  data-testid="abogado-form-notario"
                  checked={form.es_notario}
                  onCheckedChange={(v) => setForm({ ...form, es_notario: v })}
                />
              </div>
              {form.es_notario && (
                <div className="mt-4">
                  <Label className={labelCls}>Código de notario</Label>
                  <Input data-testid="abogado-form-codigo-notario" value={form.codigo_notario} onChange={(e) => setForm({ ...form, codigo_notario: e.target.value })} className={`${inputCls} mt-2`} placeholder="1234" />
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex items-center justify-between rounded-lg bg-white border border-[#E5DFD3] p-4">
              <div>
                <div className="text-sm font-medium text-[#1A0810]">Activo</div>
                <div className="text-xs text-[#6B5D57] mt-0.5">Los inactivos no aparecen en asignaciones.</div>
              </div>
              <Switch
                data-testid="abogado-form-activo"
                checked={form.activo}
                onCheckedChange={(v) => setForm({ ...form, activo: v })}
              />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="abogado-save-button" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar abogado</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Está seguro de eliminar a <b>{deleteTarget?.nombre}</b>? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="abogado-confirm-delete" className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
